import { useEffect, useState } from 'react';
import Api from 'services/api';
import { BiddingData } from 'store/modules/bidding_store/types';

const cache = new Map<number, BiddingData>();

export function useBiddingDetail(biddingId: number) {
  const [bidding, setBidding] = useState<BiddingData | null>(() => cache.get(biddingId) ?? null);
  const [loading, setLoading] = useState(() => !cache.has(biddingId));
  const [error, setError] = useState(false);

  useEffect(() => {
    const cached = cache.get(biddingId);
    if (cached) {
      setBidding(cached);
      setLoading(false);
      setError(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(false);
    Api.get(`/biddings/${biddingId}.json`)
      .then((response) => {
        if (!active) return;
        if (response?.status === 200 && (response.data?.bidding_id || response.data?.id)) {
          cache.set(biddingId, response.data);
          setBidding(response.data);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [biddingId]);

  const patch = (updater: (current: BiddingData) => BiddingData) => {
    setBidding((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      cache.set(biddingId, next);
      return next;
    });
  };

  return { bidding, loading, error, patch };
}
